<template>
  <v-dialog :value="opened" scrollable persistent max-width="900">
    <v-card v-if="opened">
      <v-card-title class="pb-1 px-3 d-flex flex-nowrap align-start">
        <v-icon x-large class="mr-3">mdi-file-document-edit-outline</v-icon>
        <span class="flex-grow-1 text-justify align-self-center" style="word-break: normal !important">
          {{ value.id ? "Editar FIC" : "Nova FIC" }} - {{ beneficioLabel }}
        </span>
        <v-chip
          v-if="value.travado"
          small
          color="red"
          text-color="white"
          class="mr-2 align-self-center"
        >
          <v-icon small left>mdi-lock</v-icon>
          Travada
        </v-chip>
        <v-btn icon @click="close">
          <v-icon>mdi-close-circle-outline</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pb-0" style="max-height: 70vh; overflow-y: auto">
        <v-form ref="form" v-model="formValid">
          <!-- LI Sections -->
          <template v-if="isLI">
            <SectionTitle title="Dados Gerais" />
            <v-row>
              <v-col
                v-for="(card, k) in fieldsLI.quantidadesCalculadas"
                :key="'qc-' + k"
                cols="6"
                sm="4"
                class="pl-4"
              >
                <v-text-field
                  :value="value[card.key]"
                  :label="card.name"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                  :readonly="!!value.travado"
                  @input="updateValue(card.key, $event !== '' ? parseInt($event) : null)"
                />
              </v-col>
            </v-row>

            <SectionTitle :title="'Faturamento Bruto (R$) – Ano base ' + value.ano_base" />
            <v-row>
              <input-v
                v-for="(col, k) in fieldsLI.faturamento"
                :key="'fat-' + k"
                class="pl-4"
                :value="value[col.key]"
                :label="col"
                :colSize="col.colSize"
                :editable="!value.travado"
                @input="updateValue(col.key, $event)"
              />
            </v-row>

            <SectionTitle :title="'Exportações (R$) – Ano base ' + value.ano_base" />
            <v-row>
              <input-v
                v-for="(col, k) in fieldsLI.exportacoes"
                :key="'exp-' + k"
                class="pl-4"
                :value="value[col.key]"
                :label="col"
                :colSize="col.colSize"
                :editable="!value.travado"
                @input="updateValue(col.key, $event)"
              />
            </v-row>

            <SectionTitle :title="'Importações (R$) – Ano base ' + value.ano_base" />
            <v-row>
              <input-v
                v-for="(col, k) in fieldsLI.importacoes"
                :key="'imp-' + k"
                class="pl-4"
                :value="value[col.key]"
                :label="col"
                :colSize="col.colSize"
                :editable="!value.travado"
                @input="updateValue(col.key, $event)"
              />
            </v-row>
          </template>

          <!-- LB Sections -->
          <template v-else>
            <SectionTitle title="Representante" />
            <v-row>
              <input-v
                v-for="(col, k) in fieldsLB.representante"
                :key="'rep-' + k"
                class="pl-4"
                :value="value[col.key]"
                :label="col"
                :colSize="col.colSize"
                @input="updateValue(col.key, $event)"
              />
            </v-row>

            <SectionTitle title="Dados da Empresa" />
            <v-row>
              <template v-for="(col, k) in fieldsLB.empresa">
                <input-v
                  v-if="shouldShowField(col)"
                  :key="'emp-' + k"
                  class="pl-4"
                  :value="value[col.key]"
                  :label="col"
                  :colSize="col.colSize"
                  @input="updateValue(col.key, $event)"
                />
              </template>
            </v-row>

            <SectionTitle title="Dispêndios" />
            <v-row>
              <input-v
                v-for="(col, k) in fieldsLB.dispendios"
                :key="'disp-' + k"
                class="pl-4"
                :value="value[col.key]"
                :label="col"
                :colSize="col.colSize"
                @input="updateValue(col.key, $event)"
              />
            </v-row>

            <SectionTitle title="Programa de Formação" />
            <v-row>
              <template v-for="(col, k) in fieldsLB.formacao">
                <input-v
                  v-if="shouldShowField(col)"
                  :key="'form-' + k"
                  class="pl-4"
                  :value="value[col.key]"
                  :label="col"
                  :colSize="col.colSize"
                  @input="updateValue(col.key, $event)"
                />
              </template>
            </v-row>
          </template>
        </v-form>

        <v-alert
          v-if="errorMessage"
          dense
          style="font-size: 13px"
          text
          type="error"
          class="mt-2"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <v-btn
          v-if="isLI && value.id"
          small
          :color="value.travado ? 'green' : 'red'"
          :dark="!!value.id"
          :loading="travando"
          @click="$emit('travar')"
        >
          <v-icon small left>{{ value.travado ? 'mdi-lock-open' : 'mdi-lock' }}</v-icon>
          {{ value.travado ? 'Destravar' : 'Travar' }}
        </v-btn>
        <v-spacer />
        <v-btn text @click="close">Cancelar</v-btn>
        <v-btn
          color="#1c1c44"
          dark
          @click="save"
          :loading="saving"
          :disabled="value.travado"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { BeneficiosEnum, BeneficiosLabels } from "@/core/enums/beneficios";
import { getFieldsLI, getFieldsLB } from "@/templates/dispendio/fic.fields";

const SectionTitle = {
  functional: true,
  props: { title: String },
  render(h, ctx) {
    return h(
      "div",
      { class: "subtitle-1 font-weight-bold mt-4 mb-2", style: "color: #1c1c44" },
      ctx.props.title
    );
  },
};

export default {
  components: {
    SectionTitle,
    "input-v": () => import("@/components/input-v.vue"),
  },
  props: {
    opened: { type: Boolean, default: false },
    value: { type: Object, default: () => ({}) },
    beneficio: { type: Number, required: true },
    anoBase: { type: [Number, String], default: null },
    errorMessage: { type: String, default: "" },
    travando: { type: Boolean, default: false },
  },
  data() {
    return {
      formValid: true,
      saving: false,
    };
  },
  computed: {
    isLI() {
      return this.beneficio === BeneficiosEnum.LEI_DE_INFORMATICA;
    },
    beneficioLabel() {
      return BeneficiosLabels[this.beneficio] || "";
    },
    fieldsLI() {
      return getFieldsLI(this.$fieldTypes);
    },
    fieldsLB() {
      return getFieldsLB(this.$fieldTypes);
    },
  },
  methods: {
    updateValue(key, val) {
      this.$emit("update:value", { ...this.value, [key]: val });
    },
    shouldShowField(col) {
      if (!col.showWhen) return true;
      return !!this.value[col.showWhen];
    },
    close() {
      this.$emit("update:opened", false);
    },
    save() {
      if (this.$refs.form && !this.$refs.form.validate()) return;

      this.saving = true;
      const promise = new Promise((resolve) => {
        this.$emit("save", { ...this.value });
        resolve();
      });
      promise.finally(() => {
        this.saving = false;
      });
    },
  },
};
</script>
